import {
  SENDING_DOMAIN_REGIONS,
  sendingDomainPill,
  type SendingDomainHeldBroadcastDto,
  type SendingDomainRateLimitsDto,
  type SendingDomainRateWindowDto,
  type SendingDomainStoppedDto,
  type SendingDomainViewDto,
  type SendingDomainWarmupStepDto,
  type SendingDomainWarmupSummaryDto,
  type SendingDomainWarmupSummaryItemDto,
  type SendingDomainWarmupTodayDto,
  type SendingDomainWarmupViewDto,
  type WebsiteCheckStatus,
} from '@/types/sendingDomains'
import { isHostCoveredBy } from './domainHost'
import { parseServerDate } from './time'
import {
  formatCount,
  formatLimit,
  localShortDay,
  localTimeWithZone,
  moveUpRuleSentence,
  stepLimitsSentence,
  utcDayLabel,
} from './sendingDomainWarmup'

// Every rule and sentence the two sending-domain surfaces show about a domain (ORB-953). The legacy Settings screens
// and the console's Admin screens both read them here, so the two say the same thing in the same words. Where the two
// designs word something differently, the function takes the smallest parameter that says how, never a second copy;
// each surface keeps its own components and styling. Every number comes from the API; these only put the numbers into
// sentences.

/** One line of a domain's status: a lead-in the screens set in bold, then the rest of the sentence. */
export interface DomainStatusLine {
  lead?: string
  text: string
}

/** A run of a sentence: a `strong` run is set in bold and a `code` run as code, each surface in its own style. */
export interface CopyRun {
  text: string
  strong?: boolean
  code?: boolean
}

/** Where each surface sends someone to set the company's website. */
export const COMPANY_WEBSITE_PLACE = {
  settings: 'in Company settings',
  console: 'on the Company page',
} as const

export type CompanyWebsitePlace = (typeof COMPANY_WEBSITE_PLACE)[keyof typeof COMPANY_WEBSITE_PLACE]

function runsText(runs: CopyRun[]): string {
  return runs.map(run => run.text).join('')
}

// Reasons arrive from the API as standalone sentences ("Hard bounces 3.4% ..."); the screens read them after a colon.
function lowerFirst(text: string): string {
  return text.charAt(0).toLowerCase() + text.slice(1)
}

function sameLocalDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
}

const WEBSITE_CHECK_STATUSES: readonly string[] = ['NotChecked', 'Passed', 'Failed']

/** The website check result, or null (logged) for a value this build does not know. */
export function websiteCheckStatus(domain: Pick<SendingDomainViewDto, 'WebsiteCheckStatus'>): WebsiteCheckStatus | null {
  if (!WEBSITE_CHECK_STATUSES.includes(domain.WebsiteCheckStatus)) {
    console.error('Unknown website check status', domain.WebsiteCheckStatus)
    return null
  }
  return domain.WebsiteCheckStatus as WebsiteCheckStatus
}

/** Where the DNS check stands, read from Resend's verification status. */
export type DnsCheckOutcome = 'Passed' | 'Pending' | 'Failed'

const DNS_CHECK_OUTCOMES: Record<string, DnsCheckOutcome> = {
  Verified: 'Passed',
  NotStarted: 'Pending',
  Pending: 'Pending',
  PartiallyVerified: 'Pending',
  TemporaryFailure: 'Pending',
  Failed: 'Failed',
  PartiallyFailed: 'Failed',
}

/** The DNS check result, or null (logged) for a status this build does not know. */
export function dnsCheckOutcome(domain: Pick<SendingDomainViewDto, 'Status'>): DnsCheckOutcome | null {
  if (!Object.prototype.hasOwnProperty.call(DNS_CHECK_OUTCOMES, domain.Status)) {
    console.error('Unknown sending domain status', domain.Status)
    return null
  }
  return DNS_CHECK_OUTCOMES[domain.Status]
}

/** A strict subdomain of the company website: what the add screen recommends and the website check passes unasked. */
function isSubdomainOf(domain: string, companyDomain: string | null): boolean {
  return !!companyDomain && domain !== companyDomain && isHostCoveredBy(domain, [companyDomain])
}

export function dnsRecordCounts(domain: Pick<SendingDomainViewDto, 'DnsRecords'>): { verified: number; total: number } {
  return {
    verified: domain.DnsRecords.filter(record => record.Status === 'Verified').length,
    total: domain.DnsRecords.length,
  }
}

/** How many of the two checks a setting-up domain has passed: its DNS records, and its website. */
export function setupChecksPassed(domain: SendingDomainViewDto): number {
  return (domain.Status === 'Verified' ? 1 : 0) + (domain.WebsiteCheckStatus === 'Passed' ? 1 : 0)
}

/** The step a warming domain is on, with its limits. */
export interface WarmupStep {
  step: number
  lastStep: number
  dailyLimit: number
  hourlyLimit: number | null
  qualifyingVolume: number
}

/** The step a warming (or holding) domain is on. The API sets all three while it warms, so a missing one is logged. */
export function warmupStep(domain: SendingDomainViewDto): WarmupStep | null {
  const { WarmupStage: step, DailyLimit: dailyLimit, QualifyingVolume: qualifyingVolume } = domain
  if (step == null || dailyLimit == null || qualifyingVolume == null) {
    console.error('Warming sending domain has no warm-up step', domain.Id)
    return null
  }
  return { step, lastStep: domain.WarmupLastStep, dailyLimit, hourlyLimit: domain.HourlyLimit ?? null, qualifyingVolume }
}

/** Which rate stopped a domain. */
export type DomainStopCause = 'hardBounces' | 'complaints' | 'both'

/**
 * The rate that stopped the domain, from the flags the API sets with the stop and clears on resume or restart, never
 * from StoppedReason's words. Null (logged) when the API did not say.
 */
export function domainStopCause(domain: { StoppedOnHardBounces: boolean; StoppedOnComplaints: boolean }): DomainStopCause | null {
  if (domain.StoppedOnHardBounces && domain.StoppedOnComplaints) return 'both'
  if (domain.StoppedOnHardBounces) return 'hardBounces'
  if (domain.StoppedOnComplaints) return 'complaints'
  console.error('Stopped sending domain carries no stop cause')
  return null
}

/** " (limit 3%)", " (limit 0.05%)" or " (limits 3% and 0.05%)": the stop limit the domain went over. */
function stopLimitClause(domain: SendingDomainViewDto, limits: SendingDomainRateLimitsDto): string {
  const bounces = formatLimit(limits.StopHardBouncePercent)
  const complaints = formatLimit(limits.StopComplaintPercent)
  switch (domainStopCause(domain)) {
    case 'hardBounces':
      return ` (limit ${bounces})`
    case 'complaints':
      return ` (limit ${complaints})`
    case 'both':
      return ` (limits ${bounces} and ${complaints})`
    case null:
      return ''
  }
}

/** "up to 700 a day, 50 an hour" (no hourly part on the steps that have none). */
function limitsPhrase(dailyLimit: number, hourlyLimit?: number | null): string {
  return `up to ${formatCount(dailyLimit)} a day${hourlyLimit ? `, ${formatCount(hourlyLimit)} an hour` : ''}`
}

/** A region's label ("🇮🇪 EU — Ireland (eu-west-1)"), or its code for a region this build does not list. */
export function regionLabel(region: string | null | undefined): string {
  if (!region) return 'Not set'
  return SENDING_DOMAIN_REGIONS.find(option => option.value === region)?.label ?? region
}

/** "4,120 broadcast emails", "1 broadcast email". */
export function broadcastEmailCount(count: number): string {
  return `${formatCount(count)} broadcast ${count === 1 ? 'email' : 'emails'}`
}

/** "at **150** emails a day and has no limit after **11 steps**, each needing a day that sends at least half its limit". */
function warmupLengthRuns(steps: SendingDomainWarmupStepDto[]): CopyRun[] | null {
  const first = steps[0]
  if (!first) {
    console.error('Sending domain warm-up has no steps')
    return null
  }
  return [
    { text: 'at ' },
    { text: formatCount(first.DailyLimit), strong: true },
    { text: ' emails a day and has no limit after ' },
    { text: `${steps.length} steps`, strong: true },
    { text: ', each needing a day that sends at least half its limit' },
  ]
}

/** "A new domain starts at 150 emails a day and has no limit after 11 steps, each needing a day that sends at least half its limit." */
export function newDomainWarmupSentence(steps: SendingDomainWarmupStepDto[]): string {
  const length = warmupLengthRuns(steps)
  return length ? `A new domain starts ${runsText(length)}.` : ''
}

/** "Once it passes, e-ortec.com starts warming up at 150 emails a day and has no limit after 11 steps, each ...". */
export function warmsUpOncePassedSentence(domainName: string, steps: SendingDomainWarmupStepDto[]): string {
  const length = warmupLengthRuns(steps)
  return length ? `Once it passes, ${domainName} starts warming up ${runsText(length)}.` : ''
}

function heldEmailsPhrase(heldEmails: number): string {
  return heldEmails === 0 ? 'No broadcast emails held.' : `${broadcastEmailCount(heldEmails)} held.`
}

/** "Holding at 1,000 a day: hard bounces 2.4% (moves up at 2% or less). Still sending up to 1,000 a day." */
export function holdingLine(domain: SendingDomainViewDto): DomainStatusLine | null {
  const step = warmupStep(domain)
  if (!step || !domain.WarmupHoldReason) return null
  const daily = formatCount(step.dailyLimit)
  return {
    lead: `Holding at ${daily} a day:`,
    text: `${lowerFirst(domain.WarmupHoldReason)}. Still sending up to ${daily} a day.`,
  }
}

/** How a list lays out a domain's status lines: the two list designs differ only in these. */
export interface ListLineStyle {
  /** Where the company's website is set, for a domain the website check has nothing to compare against. */
  place: CompanyWebsitePlace
  /**
   * The Settings list runs a line's parts together ("412 sent today · resets at 02:00 CEST") and leaves a closing
   * fragment open ("no limits"); the console list closes each part as a sentence and puts the fix on its own line.
   */
  runOn: boolean
}

function setupLines(domain: SendingDomainViewDto, companyDomain: string | null, style: ListLineStyle): DomainStatusLine[] {
  const website = websiteCheckStatus(domain)
  const dnsPending = domain.Status !== 'Verified'
  const lines: DomainStatusLine[] = []

  if (website === 'Failed') {
    const lead = 'Website check failed:'
    const name = domain.Domain
    if (!companyDomain) {
      lines.push({ lead, text: `set the company's Website ${style.place}, so ${name} can be checked against it.` })
    } else {
      const failed = `${name} doesn't redirect to ${companyDomain}, so it can't send.`
      const fix = `Set it to redirect to ${companyDomain}, or add a subdomain of ${companyDomain} instead.`
      lines.push(...(style.runOn ? [{ lead, text: `${failed} ${fix}` }] : [{ lead, text: failed }, { text: fix }]))
    }
  }

  if (dnsPending) {
    const { verified, total } = dnsRecordCounts(domain)
    lines.push({
      lead: 'DNS records:',
      text: website === 'Failed' ? `${verified} of ${total} verified.` : `${verified} of ${total} verified, so it can't send yet.`,
    })
  } else if (website === 'NotChecked') {
    lines.push({ lead: 'Website check not run yet:', text: 'open it and press Check again.' })
  }
  return lines
}

function warmingLines(
  domain: SendingDomainViewDto,
  summary: SendingDomainWarmupSummaryDto | null,
  item: SendingDomainWarmupSummaryItemDto | null,
  runOn: boolean,
): DomainStatusLine[] {
  const step = warmupStep(domain)
  if (!step) return []

  let today = ''
  if (summary && item?.TodayDispatched != null) {
    const sent = `${formatCount(item.TodayDispatched)} sent today`
    const resets = `resets at ${localTimeWithZone(summary.ResetsAtUtc)}`
    today = runOn ? ` ${sent} · ${resets}` : ` ${sent}, ${resets}.`
  }
  const lines: DomainStatusLine[] = [{
    lead: `Step ${step.step} of ${step.lastStep}:`,
    text: `${limitsPhrase(step.dailyLimit, step.hourlyLimit)}.${today}`,
  }]
  if (summary) lines.push({ text: moveUpRuleSentence(step.qualifyingVolume, summary.RateLimits) })
  return lines
}

function stoppedLines(
  domain: SendingDomainViewDto,
  summary: SendingDomainWarmupSummaryDto | null,
  item: SendingDomainWarmupSummaryItemDto | null,
): DomainStatusLine[] {
  if (!domain.StoppedAtUtc) {
    console.error('Stopped sending domain has no stop time', domain.Id)
    return []
  }
  const day = localShortDay(domain.StoppedAtUtc)
  // The limit it went over comes with the summary's rate limits, so like today's figures it waits for them.
  const limit = summary ? stopLimitClause(domain, summary.RateLimits) : ''
  const lines: DomainStatusLine[] = [domain.StoppedReason
    ? { lead: `Stopped ${day}:`, text: `${lowerFirst(domain.StoppedReason)}${limit}.` }
    : { text: `Stopped ${day}.` }]

  if (item?.HeldEmails != null && item.ResumeDailyLimit != null) {
    lines.push({
      text: `${heldEmailsPhrase(item.HeldEmails)} Emails to people who registered keep sending. `
        + `A company admin can resume it at ${formatCount(item.ResumeDailyLimit)} a day.`,
    })
  }
  return lines
}

/**
 * The lines under a domain's state pill in a list. Figures from the warm-up summary (today's sends, held emails, the
 * move-up rule, the stop limit) are left out while it loads or if it failed, rather than shown as zero.
 */
export function domainListLines(
  domain: SendingDomainViewDto,
  companyDomain: string | null,
  summary: SendingDomainWarmupSummaryDto | null,
  style: ListLineStyle,
): DomainStatusLine[] {
  const item = summary?.Domains.find(entry => entry.SendingDomainId === domain.Id) ?? null
  switch (sendingDomainPill(domain)) {
    case 'SettingUp':
      return setupLines(domain, companyDomain, style)
    case 'Warming':
      return warmingLines(domain, summary, item, style.runOn)
    case 'Holding': {
      const line = holdingLine(domain)
      return line ? [line] : []
    }
    case 'Ready':
      return [{ lead: 'Warm-up complete:', text: style.runOn ? 'no limits' : 'no limits.' }]
    case 'Stopped':
      return stoppedLines(domain, summary, item)
    case null:
      return []
  }
}

/** The line that names a domain's page: "Setting up, 1 of 2 checks passed", "Warming, step 4 of 11", ... */
export function domainPageSubtitle(domain: SendingDomainViewDto): string {
  const pill = sendingDomainPill(domain)
  const step = pill === 'Warming' || pill === 'Holding' ? warmupStep(domain) : null
  switch (pill) {
    case 'SettingUp':
      return `Setting up, ${setupChecksPassed(domain)} of 2 checks passed`
    case 'Warming':
      return step ? `Warming, step ${step.step} of ${step.lastStep}` : ''
    case 'Holding':
      return step ? `Holding at step ${step.step} of ${step.lastStep}` : ''
    case 'Stopped':
      return domain.WarmupStage == null ? 'Stopped after warm-up' : `Stopped at step ${domain.WarmupStage} of ${domain.WarmupLastStep}`
    case 'Ready':
      return 'Ready: warm-up complete'
    case null:
      return ''
  }
}

/** When a domain was last checked, as its page lists it: "Today, 11:40 CEST", or "Sat 26 Sep, 11:40 CEST". */
export function checkedAtPhrase(utc: string, now: Date = new Date()): string {
  const date = parseServerDate(utc)
  if (!date) {
    console.error('Unparseable timestamp', utc)
    return ''
  }
  return `${sameLocalDay(date, now) ? 'Today' : localShortDay(utc)}, ${localTimeWithZone(utc)}`
}

/**
 * "Can't send yet: visiting e-ortec.com doesn't take people to ortec.com. Activations using e-ortec.com stay off
 * until it does. Or send from events.ortec.com instead."
 */
export function setupVerdict(
  domain: SendingDomainViewDto,
  companyDomain: string | null,
  recommendedDomain: string | null,
  place: CompanyWebsitePlace,
): string {
  const name = domain.Domain
  const website = websiteCheckStatus(domain)
  const dnsPending = domain.Status !== 'Verified'
  const reasons: { clause: string; until: string }[] = []

  if (dnsPending) reasons.push({ clause: `${name}'s DNS records aren't all verified`, until: 'until they are' })
  if (website === 'Failed') {
    reasons.push(companyDomain
      ? { clause: `visiting ${name} doesn't take people to ${companyDomain}`, until: 'until it does' }
      : { clause: `the company has no Website ${place} to check ${name} against`, until: 'until it has one' })
  } else if (website === 'NotChecked' && !dnsPending) {
    reasons.push({ clause: 'its website check has not run yet', until: 'until it passes' })
  }
  if (reasons.length === 0) return ''

  const until = reasons.length > 1 ? 'until both checks pass' : reasons[0].until
  const verdict = `Can't send yet: ${reasons.map(reason => reason.clause).join(', and ')}. Activations using ${name} stay off ${until}.`
  return website === 'Failed' && recommendedDomain ? `${verdict} Or send from ${recommendedDomain} instead.` : verdict
}

/** The DNS check's line: "All 3 records are published and verified.", or what is still missing and what to do about it. */
export function dnsCheckSentence(domain: SendingDomainViewDto, outcome: DnsCheckOutcome): string {
  const { verified, total } = dnsRecordCounts(domain)
  if (outcome === 'Passed') return `All ${formatCount(total)} records are published and verified.`
  if (outcome === 'Failed') {
    return `${verified} of ${total} verified. The rest weren't found: check them at your DNS provider against the records below, `
      + 'then press Check again.'
  }
  return domain.Status === 'NotStarted'
    ? `Add the ${total} records below at your DNS provider, then press Check again.`
    : `${verified} of ${total} verified. DNS changes can take from a few minutes to a few hours to show.`
}

/** "It runs when you press Check again.", and why a subdomain of the company website passes it anyway. */
export function websiteNotCheckedSentence(domainName: string, companyDomain: string | null): string {
  const passes = isSubdomainOf(domainName, companyDomain) ? ` ${domainName} is a subdomain of ${companyDomain}, so it passes.` : ''
  return `It runs when you press Check again.${passes}`
}

/** Why a failed website check matters: "Visiting e-ortec.com has to take people to ortec.com. Spam filters ...". */
export function websiteFailedSentence(domainName: string, companyDomain: string): string {
  return `Visiting ${domainName} has to take people to ${companyDomain}. `
    + "Spam filters distrust a new look-alike domain that doesn't lead to a real company website."
}

/**
 * The company's warming or ready subdomain to send from instead of a domain that fails its website check: ready ones
 * first, then the furthest along.
 */
export function recommendedSubdomain(
  domains: SendingDomainViewDto[],
  current: SendingDomainViewDto,
  companyDomain: string | null,
): SendingDomainViewDto | null {
  const candidates = domains.filter(domain => domain.Id !== current.Id
    && isSubdomainOf(domain.Domain, companyDomain)
    && (domain.State === 'Warming' || domain.State === 'Ready'))
  const rank = (domain: SendingDomainViewDto) => (domain.State === 'Ready' ? Number.MAX_SAFE_INTEGER : domain.WarmupStage ?? 0)
  return candidates.sort((a, b) => rank(b) - rank(a))[0] ?? null
}

/** "It's already added and warming, at step 4 of 11: up to 700 a day, 50 an hour. It moves up after ..." */
export function recommendedDomainStatus(domain: SendingDomainViewDto, limits: SendingDomainRateLimitsDto | null): string {
  if (domain.State === 'Ready') return "It's already added and ready, with no limits."
  const step = warmupStep(domain)
  if (!step) return ''
  const limitsSentence = lowerFirst(stepLimitsSentence(step.step, step.lastStep, step.dailyLimit, step.hourlyLimit))
  const rule = limits ? ` It ${lowerFirst(moveUpRuleSentence(step.qualifyingVolume, limits))}` : ''
  return `It's already added and warming, at ${limitsSentence}${rule}`
}

/** "Subdomains of ortec.com pass the website check automatically, recipients recognise them, and they start with ...". */
export function subdomainsPassSentence(companyDomain: string): string {
  return `Subdomains of ${companyDomain} pass the website check automatically, recipients recognise them, `
    + `and they start with some of ${companyDomain}'s trust.`
}

/** "The trade-off: bounces and complaints from it also count against ortec.com, so send only to people who expect ...". */
export function subdomainTradeOff(companyDomain: string): Required<DomainStatusLine> {
  return {
    lead: 'The trade-off:',
    text: `bounces and complaints from it also count against ${companyDomain}, so send only to people who expect to hear from you.`,
  }
}

/** How to keep a domain whose website check fails: redirect it to the company website, then check again. */
export function redirectSteps(domainName: string, companyDomain: string): CopyRun[][] {
  return [
    [
      { text: `Where ${domainName} is registered or its DNS is hosted, add a permanent (301) redirect to ` },
      { text: `https://${companyDomain}`, code: true },
      { text: '.' },
    ],
    [{ text: 'Click ' }, { text: 'Check again', strong: true }, { text: '.' }],
  ]
}

/** What still needs a developer: DNS records not yet verified, and the redirect while the website check fails. */
export function developerTodo(
  domain: SendingDomainViewDto,
  companyDomain: string | null,
): { pendingRecords: number; needsRedirect: boolean } {
  const { verified, total } = dnsRecordCounts(domain)
  return {
    pendingRecords: total - verified,
    // Mirrors the API: the company website and its subdomains pass the check with no redirect.
    needsRedirect: domain.WebsiteCheckStatus !== 'Passed' && !(companyDomain && isHostCoveredBy(domain.Domain, [companyDomain])),
  }
}

/** "They get what is still to do: the redirect to ortec.com. Replies come to you." */
export function developerHelper(todo: { pendingRecords: number; needsRedirect: boolean }, companyDomain: string | null): string {
  const parts: string[] = []
  if (todo.pendingRecords > 0) parts.push("the records below that aren't verified yet")
  if (todo.needsRedirect) parts.push(companyDomain ? `the redirect to ${companyDomain}` : 'the redirect to the company website')
  return `They get what is still to do: ${parts.join(', and ')}. Replies come to you.`
}

/** An instant's day relative to the viewer's today: "today", "tomorrow" or "on Mon 28 Sep". */
export function relativeLocalDay(utc: string, now: Date = new Date()): string {
  const date = parseServerDate(utc)
  if (!date) {
    console.error('Unparseable timestamp', utc)
    return ''
  }
  if (sameLocalDay(date, now)) return 'today'
  const tomorrow = new Date(now)
  tomorrow.setDate(now.getDate() + 1)
  return sameLocalDay(date, tomorrow) ? 'tomorrow' : `on ${localShortDay(utc)}`
}

/** An instant in the viewer's own time: "today at 14:02 CEST", or "Sat 26 Sep at 14:02 CEST" on another day. */
function localDayAndTime(utc: string, now: Date = new Date()): string {
  const date = parseServerDate(utc)
  if (!date) {
    console.error('Unparseable timestamp', utc)
    return ''
  }
  const day = sameLocalDay(date, now) ? 'today' : localShortDay(utc)
  return `${day} at ${localTimeWithZone(utc)}`
}

/** Who last sent the developer instructions, where and when; null if nobody has. */
export function instructionsSent(
  domain: SendingDomainViewDto,
  currentUserId: string | null,
  now: Date = new Date(),
): { to: string; by: string; when: string } | null {
  if (!domain.InstructionsSentTo || !domain.InstructionsSentAtUtc) return null
  const byYou = !!currentUserId && domain.InstructionsSentByUserId === currentUserId
  return { to: domain.InstructionsSentTo, by: byYou ? 'you' : 'an admin', when: localDayAndTime(domain.InstructionsSentAtUtc, now) }
}

/** "Sent to **it@ortec.com** by you, today at 14:02 CEST." */
export function instructionsSentRuns(sent: { to: string; by: string; when: string }): CopyRun[] {
  return [{ text: 'Sent to ' }, { text: sent.to, strong: true }, { text: ` by ${sent.by}, ${sent.when}.` }]
}

function rateBlockers(window: SendingDomainRateWindowDto, limits: SendingDomainRateLimitsDto): string[] {
  const blockers: string[] = []
  if (!window.HardBouncesAllowMovingUp) blockers.push(`hard bounces are above ${formatLimit(limits.MaxHardBouncePercentToMoveUp)}`)
  if (!window.ComplaintsAllowMovingUp) blockers.push(`complaints are above ${formatLimit(limits.MaxComplaintPercentToMoveUp)}`)
  return blockers
}

/** The Warming page's headline: moving up, holding, or how much today still has to send to count. */
export function warmingVerdict(domain: SendingDomainViewDto, warmup: SendingDomainWarmupViewDto, now: Date = new Date()): string {
  const holding = holdingLine(domain)
  if (holding) return `${holding.lead} ${holding.text}`

  const today = warmup.Today
  const step = domain.WarmupStage
  if (!today || step == null) {
    console.error('Warming sending domain has no figures for today', domain.Id)
    return ''
  }

  if (today.OnTrackToMoveUp) {
    const when = `${localTimeWithZone(today.ResetsAtUtc)} ${relativeLocalDay(today.ResetsAtUtc, now)}`
    const next = warmup.Steps.find(candidate => candidate.Step === step + 1)
    return next
      ? `Sending, and on track to move up to ${formatCount(next.DailyLimit)} a day from ${when}.`
      : `Sending, and on track to finish warm-up, with no limit from ${when}.`
  }
  if (today.CountsTowardMovingUp) {
    const blockers = rateBlockers(warmup.LastThreeDays, warmup.RateLimits)
    return `Sending, but it holds at this step for now: ${blockers.join(' and ')} over the last 3 days.`
  }
  return `Sending. Today counts toward moving up once ${formatCount(today.QualifyingVolume)} have gone out; `
    + `${formatCount(today.Dispatched)} so far.`
}

/** The checks a warming or ready domain passed on its way there. */
export const BOTH_CHECKS_PASSED = 'DNS records and website, both passed'

/** "Mon 5 Oct at the earliest": the UTC day a warming domain has no limit if it moves up every day from now. */
export function noLimitFromPhrase(warmup: Pick<SendingDomainWarmupViewDto, 'NoLimitFromUtc'>): string | null {
  return warmup.NoLimitFromUtc ? `${utcDayLabel(warmup.NoLimitFromUtc)} at the earliest` : null
}

/** What today's meter says to a screen reader: "412 of 700 sent today. The mark is 350, half the limit." */
export function todayMeterLabel(today: SendingDomainWarmupTodayDto): string {
  return `${formatCount(today.Dispatched)} of ${formatCount(today.DailyLimit)} sent today. `
    + `The mark is ${formatCount(today.QualifyingVolume)}, half the limit.`
}

/** "Past the mark at 350, half the limit, so today counts toward moving up". */
export function todayMarkPhrase(today: SendingDomainWarmupTodayDto): string {
  const mark = formatCount(today.QualifyingVolume)
  return today.CountsTowardMovingUp
    ? `Past the mark at ${mark}, half the limit, so today counts toward moving up`
    : `Today counts toward moving up once it reaches ${mark}, half the limit`
}

/** A held broadcast email keeps its time of day, so a day's share does not start when the count resets. */
export const OVER_LIMIT_WAIT_SENTENCE =
  'Broadcast emails over the limit wait for the next day. They go out at the time of day their broadcast was '
  + "scheduled for, as fast as that day's limits allow."

/** Registrant emails count toward a warming domain's day but are never held for it. */
export const REGISTRANTS_NEVER_HELD: Required<DomainStatusLine> = {
  lead: 'Emails to people who registered are never held:',
  text: "confirmations, reminders and post-event follow-ups always send straight away and count toward the day's limit.",
}

/** "Everything this domain sends shares one daily limit, plus an hourly limit from step 4." */
export function sharedLimitSentence(steps: SendingDomainWarmupStepDto[]): string {
  const firstHourly = steps.find(step => step.HourlyLimit != null)
  return firstHourly
    ? `Everything this domain sends shares one daily limit, plus an hourly limit from step ${firstHourly.Step}.`
    : 'Everything this domain sends shares one daily limit.'
}

/** "Goes back to step 1, 150 a day. Broadcasts already scheduled from this domain take longer to finish." */
export function restartSentence(steps: SendingDomainWarmupStepDto[]): string {
  const first = steps[0]
  if (!first) return ''
  return `Goes back to step ${first.Step}, ${formatCount(first.DailyLimit)} a day. `
    + 'Broadcasts already scheduled from this domain take longer to finish.'
}

/**
 * Who may press a warm-up action and that it asks first. Settings opens to anyone in the company, so its pages say only
 * company admins may; the console's Admin opens only to them.
 */
function actionNote(saysAdminsOnly: boolean): string {
  return `${saysAdminsOnly ? ' Company admins only.' : ''} You'll confirm before it restarts.`
}

/** Beside Restart warm-up: where it goes back to, then who may and that it asks first. */
export function restartLine(steps: SendingDomainWarmupStepDto[], { saysAdminsOnly }: { saysAdminsOnly: boolean }): string {
  const sentence = restartSentence(steps)
  return sentence ? `${sentence}${actionNote(saysAdminsOnly)}` : ''
}

/** The question before restarting a domain's warm-up, and where it goes back to. */
export function restartConfirmation(steps: SendingDomainWarmupStepDto[]): { question: string; detail: string } {
  return { question: 'Restart warm-up?', detail: restartSentence(steps) }
}

/** "Warm-up complete: no limits. It still stops automatically if hard bounces pass 3% or complaints pass 0.05% ...". */
export function readyVerdict(limits: SendingDomainRateLimitsDto | null): string {
  return limits
    ? `Warm-up complete: no limits. It still stops automatically if hard bounces pass ${formatLimit(limits.StopHardBouncePercent)} `
      + `or complaints pass ${formatLimit(limits.StopComplaintPercent)} over the last 3 days.`
    : 'Warm-up complete: no limits.'
}

/**
 * "Stopped automatically: hard bounces 3.4% of 1,480 sent over the last 3 days (limit 3%). 4,120 broadcast emails are
 * held until a company admin resumes it."
 */
export function stoppedVerdict(domain: SendingDomainViewDto, heldEmails: number, limits: SendingDomainRateLimitsDto): string {
  const reason = domain.StoppedReason
    ? `Stopped automatically: ${lowerFirst(domain.StoppedReason)}${stopLimitClause(domain, limits)}.`
    : 'Stopped automatically.'
  const held = heldEmails === 0
    ? 'No broadcast emails are waiting on it.'
    : `${broadcastEmailCount(heldEmails)} ${heldEmails === 1 ? 'is' : 'are'} held until a company admin resumes it.`
  return `${reason} ${held}`
}

/** A stopped domain's daily limit when it stopped: "1,500 a day", or "No limit" for one that had finished warm-up. */
export function limitWhenStoppedPhrase(domain: Pick<SendingDomainViewDto, 'DailyLimit'>): string {
  return domain.DailyLimit != null ? `${formatCount(domain.DailyLimit)} a day` : 'No limit'
}

/** "From SCP Q4 invitation, 1,480 of its 5,600 already sent." */
export function heldBroadcastSource(broadcast: SendingDomainHeldBroadcastDto): string {
  return `From ${broadcast.Name}, ${formatCount(broadcast.Sent)} of its ${formatCount(broadcast.TotalAudience)} already sent.`
}

/** "They go out after you resume, starting at 1,000 a day." */
export function heldGoOutSentence(resumeDailyLimit: number): string {
  return `They go out after you resume, starting at ${formatCount(resumeDailyLimit)} a day.`
}

/** A kind of email a stop does not hold, and what the stop does to it. */
export interface WhileStoppedRow {
  effect: 'stillSending' | 'off' | 'cancelledIfLate'
  tag: string
  title: string
  text: string
}

/** The rows of "While it's stopped" after the held broadcast emails. */
export const WHILE_STOPPED_ROWS: readonly WhileStoppedRow[] = [
  {
    effect: 'stillSending',
    tag: 'Still sending',
    title: 'Emails to people who registered',
    text: 'Confirmations, reminders and post-event follow-ups keep sending as normal.',
  },
  { effect: 'off', tag: 'Off', title: 'Test sends', text: 'Back on when it resumes.' },
  {
    effect: 'cancelledIfLate',
    tag: 'Cancelled if late',
    title: 'Timed emails to uploaded lists',
    text: 'Emails like "We\'re live" that can no longer go out on time are cancelled, not sent late.',
  },
]

/** "the **A**, **B** and **C**". */
function nameRuns(names: string[]): CopyRun[] {
  return names.flatMap((name, index) => [
    ...(index === 0 ? [] : [{ text: index === names.length - 1 ? ' and ' : ', ' }]),
    { text: name, strong: true },
  ])
}

/**
 * "What to check first.": what usually causes the rate that stopped the domain, pointed at the broadcasts it holds.
 * Complaints get their own advice. Null when the API did not say which rate stopped it (domainStopCause logs it).
 */
export function checkFirstRuns(
  domain: Pick<SendingDomainViewDto, 'StoppedOnHardBounces' | 'StoppedOnComplaints'>,
  stopped: SendingDomainStoppedDto,
): CopyRun[] | null {
  const cause = domainStopCause(domain)
  if (!cause) return null

  const names = stopped.HeldBroadcasts.map(broadcast => broadcast.Name)
  const plural = names.length > 1
  const lists: CopyRun[] = names.length === 0
    ? [{ text: 'the lists you sent to recently' }]
    : [{ text: 'the lists behind the ' }, ...nameRuns(names), { text: plural ? ' broadcasts' : ' broadcast' }]
  const cancel = plural ? 'cancel those broadcasts and send them' : 'cancel the broadcast and send it'
  const held = stopped.HeldEmails > 0 ? formatCount(stopped.HeldEmails) : null
  const lead: CopyRun = { text: 'What to check first.', strong: true }

  if (cause === 'complaints') {
    return [
      lead,
      { text: ' Complaints come from people who marked the email as spam. Check that everyone on ' },
      ...lists,
      { text: ' expects to hear from you.' },
      ...(held
        ? [{ text: ` Resuming sends the ${held} held emails to the same people, so if they didn't ask to hear from you, ${cancel} again to people who did.` }]
        : []),
    ]
  }
  return [
    lead,
    { text: " Hard bounces are addresses that don't exist. Check where " },
    ...lists,
    { text: ' came from: old, bought or scraped lists are the usual cause.' },
    ...(held
      ? [{ text: ` Resuming sends the ${held} held emails to the same addresses, so if those lists haven't been cleaned recently, ${cancel} again to a cleaned list.` }]
      : []),
  ]
}

/**
 * Where resuming starts, against where the domain stopped. `stoppedDailyLimit` is the domain's own DailyLimit, which
 * a domain that stopped after finishing warm-up does not have.
 */
export function resumeStartSentence(stopped: SendingDomainStoppedDto, stoppedDailyLimit: number | null): string {
  const resumeDaily = formatCount(stopped.ResumeDailyLimit)
  if (stopped.StoppedAtStep == null) {
    return `Resuming starts at the last step, ${resumeDaily} a day, so the domain rebuilds trust on less volume.`
  }
  if (stopped.ResumeStep === stopped.StoppedAtStep) return `Resuming starts again at ${resumeDaily} a day.`
  if (stoppedDailyLimit == null) {
    console.error('Stopped sending domain has a step but no daily limit', stopped.StoppedAtStep)
    return ''
  }
  return `Resuming starts one step below the ${formatCount(stoppedDailyLimit)} a day where it stopped, `
    + 'so the domain rebuilds trust on less volume.'
}

/** Beside Resume and Restart warm-up: where each starts, then who may and that it asks first. */
export function resumeRestartLine(
  stopped: SendingDomainStoppedDto,
  stoppedDailyLimit: number | null,
  steps: SendingDomainWarmupStepDto[],
  { saysAdminsOnly }: { saysAdminsOnly: boolean },
): string {
  const first = steps[0]
  const restart = first ? ` Restart warm-up goes back to step ${first.Step}, ${formatCount(first.DailyLimit)} a day.` : ''
  return `${resumeStartSentence(stopped, stoppedDailyLimit)}${restart}${actionNote(saysAdminsOnly)}`
}

/** "After resuming it is at step 5 of 11: up to 1,000 a day, 75 an hour. It moves up after a day that sends at least 500, ..." */
export function afterResumeSentence(stopped: SendingDomainStoppedDto, lastStep: number, limits: SendingDomainRateLimitsDto): string {
  return `After resuming it is at step ${stopped.ResumeStep} of ${lastStep}: `
    + `${limitsPhrase(stopped.ResumeDailyLimit, stopped.ResumeHourlyLimit)}. `
    + `It ${lowerFirst(moveUpRuleSentence(stopped.ResumeQualifyingVolume, limits))}`
}

/** The question before resuming a stopped domain, and what resuming starts. */
export function resumeConfirmation(
  domainName: string,
  stopped: SendingDomainStoppedDto,
  lastStep: number,
): { question: string; detail: string } {
  const held = stopped.HeldEmails > 0
    ? ` Its ${formatCount(stopped.HeldEmails)} held broadcast ${stopped.HeldEmails === 1 ? 'email starts' : 'emails start'} going out again.`
    : ''
  return {
    question: `Resume ${domainName}?`,
    detail: `It resumes at step ${stopped.ResumeStep} of ${lastStep}, up to ${formatCount(stopped.ResumeDailyLimit)} a day.${held}`,
  }
}

/** "A domain stops automatically when, over the last 3 days, hard bounces pass 3% of emails sent (once at least 200 ...". */
export function stopRuleSentence(limits: SendingDomainRateLimitsDto): string {
  return 'A domain stops automatically when, over the last 3 days, '
    + `hard bounces pass ${formatLimit(limits.StopHardBouncePercent)} of emails sent (once at least ${formatCount(limits.StopMinimumSent)} have gone out) `
    + `or complaints pass ${formatLimit(limits.StopComplaintPercent)} of emails delivered (with at least ${formatCount(limits.StopMinimumComplaints)} complaints).`
}

/** Beside the complaint rate: most mailboxes never report a complaint back. */
export const COMPLAINTS_UNDERREPORTED =
  "Gmail and most business mailboxes (Microsoft 365) don't report complaints back, so 0 complaints is not proof nobody complained."

/** What a rate meter says to a screen reader: "3.4% of 1,480 sent, over the 3% limit. Holds above 2%, stops above 3%." */
export function rateMeterLabel(meter: { rate: string; outOf: string; holdAbove: number; stopAbove: number; overStopLimit: boolean }): string {
  const stop = formatLimit(meter.stopAbove)
  const over = meter.overStopLimit ? `, over the ${stop} limit` : ''
  return `${meter.rate} of ${meter.outOf}${over}. Holds above ${formatLimit(meter.holdAbove)}, stops above ${stop}.`
}

/** Above the suggestions: none of them blocks sending. */
export const SUGGESTIONS_NOTE = 'Optional. None of these affect sending.'

export interface DomainSuggestion {
  kind: 'dmarc' | 'postmaster' | 'replyTo'
  title: string
  text: string
}

/** Deliverability habits a warming or ready domain benefits from, none of them blocking a send. */
export function domainSuggestions(domainName: string, companyName: string | null): DomainSuggestion[] {
  const reader = companyName ? `someone at ${companyName}` : 'someone on your team'
  const person = companyName ? `a person at ${companyName}` : 'a person on your team'
  return [
    {
      kind: 'dmarc',
      title: 'Send DMARC reports to an inbox someone reads',
      text: `Your DMARC record says where reports on mail sent as ${domainName} go. Point its report address (rua) at an inbox ${reader} reads.`,
    },
    {
      kind: 'postmaster',
      title: `Set up Google Postmaster Tools for ${domainName}`,
      text: "Gmail shows its spam complaint rate for the domain there. It doesn't report complaints back to us.",
    },
    {
      kind: 'replyTo',
      title: 'Use a reply-to address someone reads',
      text: `People reply to invitations. Make sure those replies reach ${person}.`,
    },
  ]
}

/** What a new sending domain is: a subdomain of the company website, or a separate domain. */
export type NewDomainKind = 'subdomain' | 'separate'

/** Why a subdomain of the company website is the recommended kind of sending domain. */
export function subdomainChoiceSentence(companyDomain: string): string {
  return `Recipients recognise it, it starts with some of ${companyDomain}'s trust, and the website check passes automatically. `
    + `Use a subdomain, never ${companyDomain} itself, so your team's own email stays separate.`
}

/** What a separate domain costs: no reputation, a redirect to the company website, and spam filters' suspicion. */
export function separateDomainSentence(companyDomain: string | null): string {
  return companyDomain
    ? `For example e-${companyDomain}. It starts with no reputation, must redirect to ${companyDomain} to pass the website check, `
      + 'and spam filters treat new look-alike domains with suspicion.'
    : "It starts with no reputation, must redirect to your company's website to pass the website check, and spam filters "
      + 'treat new look-alike domains with suspicion.'
}

/** Under the region: what it decides, and that it cannot change. */
export const REGION_HELP = "Where this domain's mail is processed. Fixed once created: to change it, remove and re-add."

/** What happens next, while setting up. */
export const SETTING_UP_NEXT = "Can't send until two checks pass."

/** The two checks a new domain has to pass, for the kind of domain being added. */
export function settingUpNextLines(
  kind: NewDomainKind,
  host: string,
  companyDomain: string | null,
  place: CompanyWebsitePlace,
): DomainStatusLine[] {
  let website: string
  if (!companyDomain) website = `set your company's Website ${place} first.`
  else if (kind === 'subdomain') website = `${host || `a subdomain of ${companyDomain}`} passes automatically.`
  else website = `${host || 'the domain'} has to redirect to ${companyDomain}.`
  return [
    { lead: 'DNS records:', text: 'add the records we show you at your DNS provider, then press Check again.' },
    { lead: 'Website check:', text: website },
  ]
}

/**
 * What happens next, while warming up, as paragraphs of runs. The ones built on the summary's schedule and limits are
 * left out until it arrives.
 */
export function warmingNextParagraphs(summary: Pick<SendingDomainWarmupSummaryDto, 'Steps' | 'RateLimits'> | null): CopyRun[][] {
  const always: CopyRun[][] = [
    [{ text: `Broadcasts over the day's limit wait for the next day. ${REGISTRANTS_NEVER_HELD.lead} ${REGISTRANTS_NEVER_HELD.text}` }],
    [{
      text: 'An email timed to an event start, like a "Live now" email to an uploaded list, can only go from a Warming domain '
        + 'if the list fits what the domain can send in one hour. Otherwise send it from a Ready domain.',
    }],
  ]
  if (!summary) return always

  const limits = summary.RateLimits
  const length = warmupLengthRuns(summary.Steps)
  const firstHourly = summary.Steps.find(step => step.HourlyLimit != null)
  const paragraphs: CopyRun[][] = []
  if (length) {
    paragraphs.push([
      { text: 'It starts ' },
      ...length,
      {
        text: `, while hard bounces stay at ${formatLimit(limits.MaxHardBouncePercentToMoveUp)} or less and complaints at `
          + `${formatLimit(limits.MaxComplaintPercentToMoveUp)} or less over the last 3 days.`,
      },
    ])
  }
  if (firstHourly?.HourlyLimit != null) {
    paragraphs.push([{ text: `From step ${firstHourly.Step} there is also an hourly limit, starting at ${formatCount(firstHourly.HourlyLimit)}.` }])
  }
  return [
    ...paragraphs,
    ...always,
    [{
      text: `If hard bounces pass ${formatLimit(limits.StopHardBouncePercent)} or complaints pass ${formatLimit(limits.StopComplaintPercent)} `
        + 'over the last 3 days, it stops and broadcasts are held until a company admin resumes it.',
    }],
  ]
}

/** What happens next, once Ready. */
export const READY_NEXT = 'No limits.'
